import React from 'react';
import { useDeleteWithConfirmController, useRecordContext, useResourceContext } from 'react-admin';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { LoadingLabel, filledButtonSx, outlinedButtonSx } from './buttons';

/**
 * «Eliminar» con confirmación, como `DeleteWithConfirmButton` (mismo controlador de react-admin,
 * modo pesimista), pero con un diálogo propio para que «Confirmar» muestre un spinner mientras se
 * borra: el `Confirm` de react-admin solo deshabilita los botones.
 *
 * Con `iconOnly` es solo la papelera (columna «Acciones» del listado), con `label` como nombre
 * accesible y tooltip.
 */
const DeleteNewsPostButton = ({ redirect = 'list', label = 'Eliminar', iconOnly = false, ...buttonProps }) => {
    const record = useRecordContext();
    const resource = useResourceContext();
    const { open, isLoading, handleDialogOpen, handleDialogClose, handleDelete } = useDeleteWithConfirmController({
        record,
        resource,
        redirect,
        mutationMode: 'pessimistic',
    });

    // Igual que en «Volver a traducir»: `isLoading` llega un render tarde y un doble clic en
    // «Confirmar» lanzaba dos DELETE. El ref lo bloquea al momento y se libera al cerrar el diálogo
    // (éxito o error).
    const inFlight = React.useRef(false);
    React.useEffect(() => {
        if (!open) inFlight.current = false;
    }, [open]);
    const confirm = (event) => {
        if (inFlight.current) return;
        inFlight.current = true;
        handleDelete(event);
    };

    if (!record) return null;

    return (
        <>
            {iconOnly ? (
                <Tooltip title={label}>
                    <span>
                        <IconButton aria-label={label} color="error" size="small" onClick={handleDialogOpen} disabled={isLoading} {...buttonProps}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            ) : (
                <Button color="error" onClick={handleDialogOpen} disabled={isLoading} {...buttonProps}>
                    {label}
                </Button>
            )}
            <Dialog
                open={open}
                onClose={isLoading ? undefined : handleDialogClose}
                // El diálogo es un portal, pero sus clics suben por el árbol de React hasta la fila
                // del listado (rowClick): se cortan aquí.
                onClick={(event) => event.stopPropagation()}
                aria-labelledby="delete-news-post-title"
            >
                <DialogTitle id="delete-news-post-title">Eliminar publicación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        La publicación dejará de mostrarse en el pop-up y en la página de novedades del ATS. ¿Seguro que
                        quieres eliminarla?
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={handleDialogClose} disabled={isLoading} color="inherit" sx={outlinedButtonSx}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirm}
                        disabled={isLoading}
                        variant="contained"
                        color="error"
                        sx={filledButtonSx}
                        aria-busy={isLoading}
                        aria-label="Confirmar"
                    >
                        <LoadingLabel loading={isLoading}>Confirmar</LoadingLabel>
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DeleteNewsPostButton;
